//@flow

import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, type TFunction, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { Loader } from '@locus-taxy/neo-ui';

import theme from './LeftBar.scss';
import ZendeskWidgetContainer from 'common/components/core/common/ZendeskWidgetContainer';
import { TicketTypeEnum } from 'common/enums/ZendeskEnum';
import StringUtils from 'common/utils/StringUtils';
import { clientSettingsSelectorByClientId } from 'common/redux/selectors/clientSettingsSelector';
import {
    getTicketingURL,
    getExtraTicketItems
} from 'common/redux/selectors/clientSelector';
import { getPlatformSettings } from 'common/modules/platform-settings';
import intelliSortIds from 'common/tests/intellisort/intelliSortIds.json';
import rosterIds from 'common/tests/roster/rosterIds.json';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';
import { personnelBUTeamsSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { TeamStatusEnum } from 'common/enums/TeamEnum';
import { getSectionVisibilitySettings } from 'common/redux/selectors/personnelSelectors';
import { teamIdsSelector } from 'common/redux/selectors/queryParamsSelectors';
import { withRouter, type Location } from 'common/components/core/router';
import { Personnel } from 'common/models/internal/Personnel';
import { ClientSettings } from 'common/models/internal/ClientSettings';
import {
    TicketingProviderItems,
    SectionVisibilitySettings
} from 'common/models/internal/Client';
import LeftBarItem from './LeftBarItem';
import { SettingsLink } from 'container/containers/settings';
import LeftBarIcon from './LeftBarIcon';

import ClientConfigService from 'common/services/ClientConfigService';
import { ToastErrorBoundary, lazy } from 'common/modules/mfe';

const NotificationBell = lazy(
    () => import('platform-app/alerts/NotificationBell'),
    {
        Fallback: () => <Loader />,
        ErrorBoundary: ({ children }) => {
            const { t } = useTranslation('alertNotifications');
            return (
                <ToastErrorBoundary
                    errorTitle={t('genericError')}
                    errorContent={t('errors.errorLoadingAlerts')}
                >
                    {children}
                </ToastErrorBoundary>
            );
        }
    }
);

const URLToActiveItemMap = {
    batch: 'planner',
    live_view: 'liveView',
    user: 'liveView',
    insights: 'insights',
    reports: 'reports',
    entity: 'entity',
    pjp: 'pjp',
    intellisort: 'intelliSort',
    quotations: 'quotations',
    orders: 'orders',
    shipments: 'shipments',
    tours: 'tours',
    territoryPlanning: 'territoryPlanning',
    netOpt: 'netOpt',
    digitalTwin: 'digitalTwin',
    settings: 'settings',
    netOptEntity: 'netOptEntity',
    roster: 'roster',
    chat: 'chat',
    contracts: 'contracts',
    notification: 'notification',
    shipflex: 'shipflex'
};

type Props = {
    theme: Object,
    personnel: Personnel,
    location: Location,
    selectedTeams: Array<string>,
    clientSettings: ClientSettings,
    ticketingURL?: string,
    extraTicketItems: Array<TicketingProviderItems>,
    visibleSections: SectionVisibilitySettings,
    children?: React.Node,
    t: TFunction,
    ...OwnProps
};

type OwnProps = {
    clientId: string
};
class LeftNavigationBar extends React.Component<Props> {
    isItemActive = item => {
        let activeItem;
        Object.keys(URLToActiveItemMap).forEach(key => {
            if (
                this.props.location.pathname.includes(
                    `/client/${this.props.clientId}/${key}`
                )
            ) {
                activeItem = URLToActiveItemMap[key];
            }
        });
        return activeItem === item;
    };

    // We need to update this to handle inactive teams case because dispatcher header doesn't use AppHeaderContainer
    createTeamSelectorQuery = (selectedTeamIds, isInsightsPage = false) => {
        if (!selectedTeamIds || selectedTeamIds.length === 0) return '';
        if (isInsightsPage) {
            // For Insights page, we don't need to modify the url
            return `?teamIds=${encodeURIComponent(selectedTeamIds.join(','))}`;
        } else {
            // For other pages, we need to remove inactive team ids from query params
            const { teams } = this.props;
            const teamsMap = {};
            teams.forEach(team => {
                teamsMap[team.teamId] = team;
            });
            const activeSelectedTeamIds = selectedTeamIds.filter(teamId => {
                const team = teamsMap[teamId];
                if (!team) return false;
                return team.get('status') !== TeamStatusEnum.Inactive;
            });
            return `?teamIds=${encodeURIComponent(
                activeSelectedTeamIds.join(',')
            )}`;
        }
    };

    render() {
        const {
            theme,
            clientId,
            selectedTeams,
            clientSettings,
            ticketingURL,
            extraTicketItems,
            visibleSections,
            alertsSettings,
            personnel,
            t
        } = this.props;
        const teamIdsQuery = this.createTeamSelectorQuery(selectedTeams);
        const teamIdsQueryForInsightsPage = this.createTeamSelectorQuery(
            selectedTeams,
            true
        );
        const isTicketingURLPresent = !StringUtils.isEmpty(ticketingURL);

        let liveViewRoute = `/client/${clientId}/live_view`;
        if (teamIdsQuery) {
            const parsedUrl = queryString.parseUrl(liveViewRoute);
            const { query: queryParams, url } = parsedUrl;
            queryParams.teamIds = decodeURIComponent(
                teamIdsQuery.split('=')[1]
            );
            liveViewRoute = `${url}?${queryString.stringify(queryParams)}`;
        }

        const oiqRoute = visibleSections?.showShipmentManagementSection
            ? `/client/${clientId}/orders/shipments`
            : `/client/${clientId}/orders`;

        return (
            <div className={theme.dashboardWrap}>
                <div className={theme.leftBar}>
                    <span className={classnames(theme.item, theme.logoWrap)}>
                        <LeftBarIcon />
                    </span>
                    {visibleSections.showInsightsSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/insights${teamIdsQueryForInsightsPage}`}
                            active={this.isItemActive('insights')}
                            tooltip={t('insights')}
                            icon="dashboard_insight_iq"
                            testId="insightsSideBar"
                        />
                    )}
                    {visibleSections.showBatchSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/batch${teamIdsQuery}`}
                            active={this.isItemActive('planner')}
                            tooltip={t('entities:plan')}
                            icon="dashboard_dispatch_iq"
                            testId="planSideBar"
                        />
                    )}
                    {visibleSections.showLiveViewSection ? (
                        <LeftBarItem
                            theme={theme}
                            to={liveViewRoute}
                            active={this.isItemActive('liveView')}
                            tooltip={t('liveView')}
                            icon="dashboard_live_view"
                            testId="liveViewSideBar"
                        />
                    ) : null}
                    {clientSettings?.enableCustomReports && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/reports`}
                            active={this.isItemActive('reports')}
                            tooltip={t('reports')}
                            icon="dashboard_custom_reports"
                            testId="reportsSideBar"
                        />
                    )}
                    {visibleSections.showPjpSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/pjp`}
                            active={this.isItemActive('pjp')}
                            tooltip={t('entities:fieldPro')}
                            icon="dashboard_fieldpro"
                            testId="fieldProSideBar"
                        />
                    )}
                    {visibleSections?.showNetOptSection && (
                        <>
                            <LeftBarItem
                                testId="digitalTwinSideBar"
                                theme={theme}
                                to={`/client/${clientId}/digitalTwin`}
                                active={this.isItemActive('digitalTwin')}
                                tooltip={t('netOpt:digitalTwin:digitalTwin')}
                                icon="dashboard_digitaltwin"
                            />
                            <LeftBarItem
                                testId="runStudySideBar"
                                theme={theme}
                                to={`/client/${clientId}/netOpt`}
                                active={this.isItemActive('netOpt')}
                                tooltip={t('entities:networkOptimization')}
                                icon="dashboard_netopt"
                            />
                            <LeftBarItem
                                testId="netOptEntitySideBar"
                                theme={theme}
                                to={`/client/${clientId}/netOptEntity`}
                                active={this.isItemActive('netOptEntity')}
                                tooltip={t('netOpt:nodeIqData')}
                                icon="netopt_entities"
                            />
                        </>
                    )}
                    {visibleSections.showEntitySection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/entity${teamIdsQuery}`}
                            active={this.isItemActive('entity')}
                            tooltip={t('masterManagement')}
                            icon="dashboard_entities"
                            testId="masterManagementSideBar"
                        />
                    )}
                    {visibleSections.showAutosortLiveViewSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/intellisort`}
                            active={this.isItemActive('intelliSort')}
                            tooltip={t('entities:intellisort')}
                            icon="dashboard_intellisort"
                            testId={intelliSortIds.leftBarIcon}
                        />
                    )}
                    {visibleSections.showPjpSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/territoryPlanning`}
                            active={this.isItemActive('territoryPlanning')}
                            tooltip={'Territory'}
                            icon="dashboard_territory"
                            testId="territorySideBar"
                        />
                    )}
                    {visibleSections.showQuoteManagementSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/quotations${teamIdsQuery}`}
                            active={this.isItemActive('quotations')}
                            tooltip={t('entities:quotation_plural')}
                            icon="file_money"
                            testId="orchestratorDashboardSideBar"
                        />
                    )}
                    {visibleSections.showOrchestrationSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/shipflex`}
                            active={this.isItemActive('shipflex')}
                            tooltip={t('orchestration:shipflex')}
                            icon="dashboard_orchestration"
                            testId="orchestrationDashboardSideBar"
                        />
                    )}
                    {visibleSections.showContractSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/contracts${teamIdsQuery}`}
                            active={this.isItemActive('contracts')}
                            tooltip={t('entities:masterContract_plural')}
                            icon="dashboard_contract_iq"
                            testId="contractsDashboardSideBar"
                        />
                    )}
                    {(visibleSections?.showOrderManagementSection ||
                        visibleSections?.showShipmentManagementSection) && (
                        <LeftBarItem
                            theme={theme}
                            to={`${oiqRoute}${teamIdsQuery}`}
                            active={this.isItemActive('orders')}
                            tooltip={t('entities:order_plural')}
                            icon="dashboard_orders"
                            testId="orderDashboardSideBar"
                        />
                    )}
                    {visibleSections?.showTourDispatchManagementSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/tours${teamIdsQuery}`}
                            active={this.isItemActive('tours')}
                            tooltip={t('entities:tourManagement')}
                            icon="dashboard_ops_iq"
                            testId="tourManagementDashboardSideBar"
                        />
                    )}
                    {visibleSections.showRosterSection && (
                        <LeftBarItem
                            theme={theme}
                            to={`/client/${clientId}/roster/shift-manager${teamIdsQuery}`}
                            active={this.isItemActive('roster')}
                            tooltip={t('entities:roster')}
                            icon="dashboard_roster"
                            testId={rosterIds.leftBarRosterIcon}
                        />
                    )}
                    <div className={theme.helpSection}>
                        {ClientConfigService.isAlertsEnabled() &&
                            alertsSettings?.isAlertsEnabled && (
                                <div>
                                    <NotificationBell />
                                </div>
                            )}
                        <SettingsLink
                            isActive={this.isItemActive('settings')}
                            theme={theme}
                        />
                        {personnel && isTicketingURLPresent ? (
                            <ZendeskWidgetContainer
                                personnel={personnel}
                                tooltipText={t('raiseTicket')}
                                theme={theme}
                                icon={'ticket'}
                                ticketType={TicketTypeEnum.EditPlanApproval}
                                dialogHeader={t('raiseTicket')}
                                ticketingURL={ticketingURL}
                                extraTicketItems={extraTicketItems}
                            />
                        ) : null}
                        {personnel ? (
                            <ZendeskWidgetContainer
                                personnel={personnel}
                                tooltipText={t('support')}
                                theme={theme}
                                icon={'help'}
                                ticketType={TicketTypeEnum.HelpAndSupport}
                                dialogHeader={t('contact')}
                            />
                        ) : null}
                    </div>
                </div>
                {this.props.children}
            </div>
        );
    }
}

const mapStateToProps = (state, props) => {
    const selectedTeams = teamIdsSelector(state, props);
    const ticketingURL = getTicketingURL(state, props);
    const extraTicketItems = getExtraTicketItems(state, props);
    const clientSettings = clientSettingsSelectorByClientId(
        state,
        props.clientId
    );
    const visibleSections = getSectionVisibilitySettings(state, props);
    const { alertsSettings } = getPlatformSettings();
    const personnel = personnelSelector(state);
    const teams = personnelBUTeamsSelector(state, props);
    return {
        personnel,
        selectedTeams,
        ticketingURL,
        extraTicketItems,
        clientSettings,
        visibleSections,
        teams,
        alertsSettings
    };
};
export default (compose(
    withRouter,
    connect(mapStateToProps),
    themr('LeftNavigationBar', theme),
    translate('translations')
)(LeftNavigationBar): React.AbstractComponent<OwnProps>);
